import { PlusCircleIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsBox from '@medflex/components/stories/SettingsBox';
import { trackCustomEvent } from '@medflex/utils/Tracking';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import Button from '@medflex/components/stories/Button';
import { useLocation } from 'react-router';
import AbsencesEdit from './AbsenceEdit';
import useSnippets from '../../../../hooks/useSnippets';
import useUserProfile from '../../../../hooks/useUserProfile';
import AbsenceView from './AbsenceView';
import AbsenceDeletePrompt from './AbsenceDeletePrompt';

const AbsenceModule = () => {
  const { t } = useTranslation('physician', { keyPrefix: 'InquiryManagementModule.Absence' });
  const absenceAnchor = useRef(null);
  const { hash } = useLocation();
  const { editContactPage, userProfile } = useUserProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState(false);
  const { snippets } = useSnippets();

  const inquiryManagementDefaultAbsenceSettings =
    userProfile?.attributes?.pages?.inquiryManagementSettings?.defaultFunnel?.absence;

  const handleFormSubmit = useCallback(
    async absence => {
      try {
        await editContactPage({
          inquiryManagementSettings: {
            defaultFunnel: {
              absence,
            },
          },
        });

        trackCustomEvent({
          eventCategory: 'physician_configuration',
          eventAction: 'button | clicked',
          eventName: `absence saved`,
        });

        setIsEditing(false);
        showSuccessToast(t('form.save_successful'));
      } catch (e) {
        showAlertToast(t('form.save_failed'));
      }
    },
    [editContactPage, t],
  );

  const handleDelete = useCallback(async () => {
    await editContactPage({
      inquiryManagementSettings: {
        defaultFunnel: {
          absence: null,
        },
      },
    });

    setIsDeletePromptOpen(false);
  }, [editContactPage]);

  useEffect(() => {
    if (hash.includes('absence') || isEditing) {
      absenceAnchor.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hash, isEditing]);

  return (
    <div>
      <h3 className="mb-5 text-xl font-semibold" ref={absenceAnchor}>
        {t('headline')}
      </h3>
      <div className="text-sm text-slate-600 mb-6">{t('subheadline')}</div>
      <div className="font-semibold mb-4">{t('title')}</div>
      {isEditing && (
        <SettingsBox>
          <AbsencesEdit
            absence={inquiryManagementDefaultAbsenceSettings}
            snippets={snippets}
            onSubmit={handleFormSubmit}
            onAbort={() => setIsEditing(false)}
          />
        </SettingsBox>
      )}
      {!isEditing && !inquiryManagementDefaultAbsenceSettings && (
        <Button kind="link" onClick={() => setIsEditing(true)}>
          <PlusCircleIcon className="h-6 w-6" />
          <span className="ml-2 hover:underline">{t('add')}</span>
        </Button>
      )}
      {!isEditing && inquiryManagementDefaultAbsenceSettings && (
        <SettingsBox
          title={inquiryManagementDefaultAbsenceSettings.name}
          onDelete={() => setIsDeletePromptOpen(true)}
          onEdit={() => setIsEditing(true)}
          canEdit
          canDelete
        >
          <AbsenceView absence={inquiryManagementDefaultAbsenceSettings} />
          {isDeletePromptOpen && (
            <AbsenceDeletePrompt
              absence={inquiryManagementDefaultAbsenceSettings}
              onDelete={handleDelete}
              onClose={() => setIsDeletePromptOpen(false)}
            />
          )}
        </SettingsBox>
      )}
    </div>
  );
};

export default AbsenceModule;
